import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getLastPushInitDiagnostics,
  getPushPermissionStatus,
  type PushInitResult,
} from '../../services/pushNotificationService';
import { usePushActivation } from '../../hooks/usePushActivation';
import { Button, Modal } from './UIComponents';
import { useGlobalPromptArbiter } from './GlobalPromptArbiter';

type ConsentStatus = 'pending' | 'dismissed' | 'granted' | 'denied';

interface ConsentState {
  status: ConsentStatus;
  updatedAt: number;
  nextEligibleAt?: number;
}

const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 7 dni
const DENIED_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 30; // 30 dni

const buildStorageKey = (uid: string) => `push-consent-onboarding:${uid}`;

const readState = (uid: string): ConsentState => {
  try {
    const raw = window.localStorage.getItem(buildStorageKey(uid));
    if (!raw) {
      return { status: 'pending', updatedAt: Date.now() };
    }
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed?.status) {
      return { status: 'pending', updatedAt: Date.now() };
    }
    return parsed;
  } catch (_error) {
    return { status: 'pending', updatedAt: Date.now() };
  }
};

const persistState = (uid: string, state: ConsentState) => {
  window.localStorage.setItem(buildStorageKey(uid), JSON.stringify(state));
};

const isSafariFamily = (ua: string): boolean =>
  /Safari/i.test(ua) && !/Chrome|CriOS|Edg|OPR|Firefox|FxiOS/i.test(ua);

const isIOS = (ua: string): boolean => /iPhone|iPad|iPod/i.test(ua);

const PushConsentOnboarding: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [info, setInfo] = useState<string | null>(null);
  const enableInFlightRef = useRef(false);
  const modalTitleId = useId();
  const { registerCandidate, unregisterCandidate, activePromptId } = useGlobalPromptArbiter();
  const { snapshot: pushSnapshot, enable: enablePush } = usePushActivation();
  const isResolvingActivation =
    pushSnapshot.uid !== currentUser?.uid || pushSnapshot.status === 'resolving';

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isSafari = useMemo(() => isSafariFamily(ua), [ua]);
  const isIOSDevice = useMemo(() => isIOS(ua), [ua]);

  const isInReviewFlow = useMemo(() => {
    if (!location.pathname.startsWith('/app/request/')) return false;
    const params = new URLSearchParams(location.search);
    return params.get('review') === '1';
  }, [location.pathname, location.search]);

  const isRequestDeepLinkContext = useMemo(() => {
    if (!location.pathname.startsWith('/app/request/')) return false;
    const params = new URLSearchParams(location.search);
    return Boolean(params.get('section') || params.get('review'));
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setIsOpen(false);
      return;
    }

    if (!location.pathname.startsWith('/app')) {
      setIsOpen(false);
      return;
    }

    const permission = getPushPermissionStatus();
    const now = Date.now();
    const existing = readState(currentUser.uid);

    if (permission === 'granted') {
      if (isResolvingActivation) {
        setIsOpen(false);
        return;
      }

      setIsOpen(false);

      if (pushSnapshot.status === 'active') {
        const next = { status: 'granted' as const, updatedAt: Date.now() };
        persistState(currentUser.uid, next);
        setStatus(next.status);
        setIsOpen(false);
        return;
      }

      if (
        existing.status === 'dismissed' &&
        existing.nextEligibleAt &&
        Date.now() < existing.nextEligibleAt
      ) {
        setStatus(existing.status);
        setIsOpen(false);
        return;
      }

      setStatus(existing.status);
      setIsOpen(true);
      return;
    }

    if (permission === 'denied') {
      const next: ConsentState =
        existing.status === 'denied'
          ? existing
          : {
              status: 'denied',
              updatedAt: now,
              nextEligibleAt: now + DENIED_COOLDOWN_MS,
            };
      persistState(currentUser.uid, next);
      setStatus(next.status);
      const shouldShow = !next.nextEligibleAt || now >= next.nextEligibleAt;
      setIsOpen(shouldShow);
      return;
    }

    if (permission === 'unsupported') {
      setStatus(existing.status);
      // Nie spamujemy modalem, ale zostawiamy możliwość pokazania instrukcji tylko na Safari iOS.
      if ((isSafari || isIOSDevice) && existing.status !== 'granted') {
        const shouldShow = !existing.nextEligibleAt || now >= (existing.nextEligibleAt || 0);
        setIsOpen(shouldShow);
      } else {
        setIsOpen(false);
      }
      return;
    }

    setStatus(existing.status);
    if (existing.status === 'granted') {
      setIsOpen(false);
      return;
    }
    if (
      existing.status === 'dismissed' &&
      existing.nextEligibleAt &&
      now < existing.nextEligibleAt
    ) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  }, [
    currentUser?.uid,
    location.pathname,
    isSafari,
    isIOSDevice,
    isResolvingActivation,
    pushSnapshot.status,
  ]);

  const onboardingBlockedByContext =
    isInReviewFlow || isRequestDeepLinkContext || location.pathname === '/app/settings';

  useEffect(() => {
    registerCandidate({
      id: 'push-consent-onboarding',
      priority: 10,
      wantsOpen: Boolean(currentUser?.uid && isOpen && !onboardingBlockedByContext),
    });
  }, [registerCandidate, currentUser?.uid, isOpen, onboardingBlockedByContext]);

  useEffect(() => {
    return () => unregisterCandidate('push-consent-onboarding');
  }, [unregisterCandidate]);

  if (!currentUser?.uid) {
    return null;
  }

  const saveAndClose = (next: ConsentState) => {
    persistState(currentUser.uid, next);
    setStatus(next.status);
    setIsOpen(false);
  };

  const handleEnable = async () => {
    if (!currentUser?.uid || enableInFlightRef.current) return;
    const permission = getPushPermissionStatus();
    if (permission === 'denied') {
      setInfo(
        'Powiadomienia są zablokowane w przeglądarce. Włącz je ręcznie w ustawieniach strony.'
      );
      return;
    }

    enableInFlightRef.current = true;
    setIsLoading(true);
    setInfo(null);
    try {
      const result: PushInitResult = await enablePush();
      const now = Date.now();

      if (result === 'enabled' || result === 'already-enabled') {
        saveAndClose({ status: 'granted', updatedAt: now });
        return;
      }

      if (result === 'permission-denied') {
        persistState(currentUser.uid, {
          status: 'denied',
          updatedAt: now,
          nextEligibleAt: now + DENIED_COOLDOWN_MS,
        });
        setStatus('denied');
        setInfo(
          'Powiadomienia są obecnie zablokowane. Możesz je włączyć ręcznie w ustawieniach przeglądarki.'
        );
        return;
      }

      if (result === 'permission-default') {
        saveAndClose({
          status: 'dismissed',
          updatedAt: now,
          nextEligibleAt: now + DISMISS_COOLDOWN_MS,
        });
        return;
      }

      const diagnostics = getLastPushInitDiagnostics();
      if (result === 'unsupported') {
        setInfo('Ta przeglądarka nie obsługuje Web Push w aktualnej konfiguracji.');
        saveAndClose({
          status: 'dismissed',
          updatedAt: now,
          nextEligibleAt: now + DISMISS_COOLDOWN_MS,
        });
        return;
      }

      if (result === 'service-worker-error') {
        setInfo(
          'Nie udało się przygotować service workera dla push. Odśwież stronę i spróbuj ponownie.'
        );
        return;
      }

      if (result === 'subscription-error') {
        setInfo('Przeglądarka zwróciła błąd odczytu subskrypcji push.');
        return;
      }

      if (result === 'missing-vapid') {
        setInfo(
          'Brak konfiguracji push po stronie aplikacji (VAPID). Skontaktuj się z administratorem.'
        );
        return;
      }

      if (result === 'brave-push-service-disabled') {
        setInfo(
          'Brave blokuje usługę push. Włącz w Brave: Ustawienia -> Prywatność i bezpieczeństwo -> "Use Google services for push messaging", zrestartuj Brave i spróbuj ponownie.'
        );
        return;
      }

      if (result === 'token-error' || result === 'no-token') {
        setInfo(
          'Nie udało się dokończyć rejestracji push na tym urządzeniu. Sprawdź ustawienia powiadomień i osłony przeglądarki dla tej strony, potem spróbuj ponownie.'
        );
        if (diagnostics) {
          console.warn('[PushConsentOnboarding] no-token diagnostics', diagnostics);
        }
        return;
      }

      if (result === 'register-error') {
        setInfo(
          'Powiadomienia zostały przygotowane w przeglądarce, ale zapis aktywacji po stronie aplikacji się nie udał. Spróbuj ponownie.'
        );
        return;
      }

      if (result === 'activation-unconfirmed') {
        setInfo(
          'Zapis aktywacji nie został potwierdzony po stronie aplikacji. Powiadomienia pozostają nieaktywne; spróbuj ponownie.'
        );
        return;
      }

      saveAndClose({
        status: 'dismissed',
        updatedAt: now,
        nextEligibleAt: now + DISMISS_COOLDOWN_MS,
      });
    } catch (error) {
      console.error('[PushConsentOnboarding] Błąd włączania powiadomień:', error);
      const diagnostics = getLastPushInitDiagnostics();
      if (diagnostics) {
        console.warn('[PushConsentOnboarding] exception diagnostics', diagnostics);
      }
      const now = Date.now();
      setInfo(
        'Nie udało się dokończyć konfiguracji push. Sprawdź ustawienia powiadomień dla tej strony i spróbuj ponownie.'
      );
      persistState(currentUser.uid, {
        status: 'dismissed',
        updatedAt: now,
        nextEligibleAt: now + DISMISS_COOLDOWN_MS,
      });
      setStatus('dismissed');
    } finally {
      enableInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const handleNotNow = () => {
    const now = Date.now();
    saveAndClose({
      status: 'dismissed',
      updatedAt: now,
      nextEligibleAt: now + DISMISS_COOLDOWN_MS,
    });
  };

  const unsupported =
    getPushPermissionStatus() === 'unsupported' || pushSnapshot.status === 'unsupported';
  const denied = status === 'denied' || getPushPermissionStatus() === 'denied';
  const grantedButNotInitialized =
    getPushPermissionStatus() === 'granted' &&
    currentUser?.uid &&
    pushSnapshot.status !== 'active' &&
    pushSnapshot.status !== 'unsupported' &&
    !isResolvingActivation;
  const shouldRender =
    isOpen &&
    !isResolvingActivation &&
    !onboardingBlockedByContext &&
    activePromptId === 'push-consent-onboarding';

  return (
    <Modal isOpen={shouldRender} onClose={handleNotNow} ariaLabelledBy={modalTitleId}>
      <div className="p-5 md:p-6">
        <h3 id={modalTitleId} className="text-lg md:text-xl font-semibold text-primary-light-100">
          Włącz powiadomienia o zleceniach
        </h3>
        <p className="mt-2 text-sm text-primary-light-300">
          Otrzymuj szybkie informacje o nowych wiadomościach i zmianach statusu.
        </p>

        {unsupported && (
          <p className="mt-3 text-sm text-primary-light-300">
            Ta przeglądarka nie wspiera teraz Web Push.
            {isIOSDevice || isSafari
              ? ' Na iPhone/iPad dodaj aplikację do ekranu głównego i uruchom ją z ikony.'
              : ''}
          </p>
        )}

        {denied && (
          <p className="mt-3 text-sm text-primary-light-300">
            Powiadomienia są zablokowane. Włącz je ręcznie w ustawieniach przeglądarki dla tej
            strony.
          </p>
        )}

        {info && <p className="mt-3 text-sm text-primary-light-300">{info}</p>}

        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          {!unsupported && !denied && !grantedButNotInitialized && (
            <Button
              variant="primary"
              onClick={handleEnable}
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              Włącz powiadomienia
            </Button>
          )}
          {grantedButNotInitialized && (
            <Button
              variant="neutral"
              onClick={handleEnable}
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              Dokończ konfigurację
            </Button>
          )}
          {denied && (
            <Button
              variant="neutral"
              onClick={() => navigate('/app/settings#notifications')}
              className="w-full sm:w-auto"
            >
              Otwórz ustawienia
            </Button>
          )}
          <Button variant="neutral" onClick={handleNotNow} className="w-full sm:w-auto">
            Nie teraz
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PushConsentOnboarding;
